import { ProductKey } from "@/components/signup/utils";

import { DEFAULT_TIMEOUT } from "../../../hooks/utils/types";
import {
    CSM_PROJECT_DEFAULTS,
    JPD_PROJECT_DEFAULTS,
    JSM_PROJECT_DEFAULTS,
    type ProjectDetails
} from "../utils/types";

export type PollingCallbacks<T = boolean> = {
    onSuccess: (startTime: number, result: T) => void;
    onError: (startTime: number, error: unknown) => void;
    onTimeout: (startTime: number) => void;
};

export type PollingOptions<T = boolean> = {
    checkFn: () => Promise<T>;
    callbacks: PollingCallbacks<T>;
    timeout?: number;
    isReady?: (result: T) => boolean;
};

/**
 * Generic polling function that can be used with any promise-returning function.
 * Implements the same polling strategy as pollCloudStatus:
 * - Initial run: check immediately
 * - Run 1: wait 15 seconds
 * - Runs 2-9: poll every 2 seconds (fast polling)
 * - Runs 10+: poll every 10 seconds (slow polling)
 *
 * @param options - Polling configuration options
 * @returns A function that starts polling when called
 */
export const createPollingFunction = <T = boolean,>({
    checkFn,
    callbacks,
    timeout = DEFAULT_TIMEOUT,
    isReady = (result: T) => Boolean(result)
}: PollingOptions<T>) => {
    const pollFunction = (startTime = Date.now(), run = 0) => {
        const poll = () => {
            const endTime = Date.now();
            const elapsedTime = endTime - startTime;

            if (elapsedTime > timeout) {
                callbacks.onTimeout(startTime);
                return;
            }

            checkFn()
                .then((result) => {
                    if (isReady(result)) {
                        callbacks.onSuccess(startTime, result);
                    } else {
                        pollFunction(startTime, run + 1);
                    }
                })
                .catch((error) => {
                    callbacks.onError(startTime, error);
                });
        };

        // initial run, check immediately to avoid rate limiting issues
        if (run === 0) {
            poll();
            return;
        }
        // we expect the majority of requests to be completed in the 15-30 second range, wait until 15 seconds to begin fast polling
        if (run === 1) {
            setTimeout(() => poll(), 15000);
            return;
        }
        // in the 15-30 seconds range, fast poll every 2 seconds
        if (run > 1 && run < 10) {
            setTimeout(() => poll(), 2000);
            return;
        }
        // if provisioning is delayed, slow poll every 10 seconds until the timeout if necessary
        setTimeout(() => poll(), 10000);
    };

    return pollFunction;
};

export const getProjectDetails = (
    productKey: ProductKey
): ProjectDetails | undefined => {
    switch (productKey) {
        case ProductKey.JIRA_CUSTOMER_SERVICE:
            return CSM_PROJECT_DEFAULTS;
        case ProductKey.JIRA_PRODUCT_DISCOVERY:
            return JPD_PROJECT_DEFAULTS;
        case ProductKey.JIRA_SERVICE_MANAGEMENT:
            return JSM_PROJECT_DEFAULTS;
        default:
            return undefined;
    }
};
